// Receipt module types (Phase 1). Numeric DB columns arrive as strings (pg
// NUMERIC), so money fields are `string | number | null` — coerce at the edge.

import { instance } from '../instance';

export type ReceiptStatus =
  | 'uploaded' | 'processing' | 'processing_failed'
  | 'needs_review' | 'submitted' | 'approved' | 'exported' | 'synced' | 'void';

export interface ReceiptLineItem {
  id?: number;
  line_no?: number;
  description: string | null;
  quantity: string | number | null;
  unit_price: string | number | null;
  amount: string | number | null;
  is_ai_generated?: boolean;
}

export interface Receipt {
  id: number;
  public_id: string;   // non-sequential UUID — used in URLs + API paths
  status: ReceiptStatus;
  vendor_name: string | null;
  vendor_address: string | null;        // full printed merchant address, if any
  receipt_date: string | null;          // 'YYYY-MM-DD'
  subtotal: string | number | null;
  tax: string | number | null;
  tip: string | number | null;          // gratuity, separate from line items
  total: string | number | null;
  currency: string;
  category_id: number | null;
  category_label?: string | null;
  ai_guessed_category_key?: string | null;  // the reader's category guess (hint when low-confidence)
  category_source?: 'memory' | 'ai' | null; // how category_id got its initial value ('memory' = this vendor before)
  category_deferred?: boolean;              // buyer chose "Not sure" — category left empty on purpose, Dawn resolves at reconcile
  purchase_class_id?: number | null;        // QBO Class (which Firefly sub-entity)
  purchase_class_label?: string | null;
  class_deferred?: boolean;                 // user chose "Not sure" — blocks; can't be bypassed
  class_source?: 'auto' | null;             // 'auto' = derived from the card by folder import (flows, badged); null = human pick
  card_id: number | null;
  card_label?: string | null;
  card_last4?: string | null;
  event_id?: string | null;                 // job-costing: the show/event this expense was for
  event_name?: string | null;
  ai_guessed_event_id?: string | null;      // what crew-shift detection auto-tagged (audit)
  needs_attention: boolean;
  attention_reasons: string[] | null;
  ai_review_perfect?: boolean;          // digest auto-filed it (needs_review→submitted) with no buyer review
  math_delta: string | number | null;
  ai_remarks: string | null;            // one-sentence reviewer note from the reader (read-only)
  notes: string | null;                 // buyer's own free-text note (editable, round-trips)
  original_filename: string | null;
  mime_type: string | null;
  digitization_error?: string | null;
  uploaded_by: string;
  uploaded_by_name?: string | null;
  owner_name?: string | null;                     // effective owner's name (owner_user_id ?? uploaded_by)
  owner_user_id?: string | null;                  // current owner (null = uploader); set on hand-off
  detected_card_owner_id?: string | null;         // read card belongs to this other user → offer hand-off
  detected_card_owner_name?: string | null;
  created_at: string;
  updated_at: string;
  submitted_at: string | null;
  approved_at: string | null;
  line_items?: ReceiptLineItem[];
  // Soft-duplicate check (post-digest): another receipt this buyer loaded that
  // looks like the same purchase. Non-blocking — surfaced as a dismissible banner.
  likely_duplicate?: {
    public_id: string;
    vendor_name: string | null;
    receipt_date: string | null;
    total: string | number | null;
    status: ReceiptStatus;
  } | null;
}

export interface CardOption {
  id: number;
  label: string;
  last4: string | null;
  network: string | null;
  owner_user_id: string | null;
  default_purchase_class_id?: number | null;      // the card's default QBO class (admin-set)
  default_purchase_class_label?: string | null;
}

export interface CategoryOption {
  id: number;
  key: string;
  label: string;
  qbo_account_ref: string | null;
  financial_only?: boolean; // bookkeeper-assign-only (reconcile board): hidden from buyer pickers + the AI reader
}

// QBO Class = which Firefly sub-entity a purchase belongs to (Drone Shows / Holdings
// / Systems / Technologies). Orthogonal to CategoryOption (the CoA account).
export interface PurchaseClassOption {
  id: number;
  key: string;
  label: string;
  is_default?: boolean;
}

export interface FormOptions {
  cards: CardOption[];
  categories: CategoryOption[];
  classes: PurchaseClassOption[];  // the class default lives on the card (folder-importer auto-derive), not here
}

// Import-verification summary returned by POST /reconciliation/import, persisted on
// the upload. Shown on the board so format drift / roster gaps are seen at import
// time, before any reconciling happens on top of a bad import.
export interface ImportSummary {
  file_rows: number;           // data rows in the CSV
  kept: number;                // purchase/credit rows read from the file
  excluded: number;            // payments/fees/unparsable rows dropped by the format
  credits: number;             // positive rows auto-resolved as refund_credit
  unknown_cards: string[];     // last4s with no company_cards roster entry
  repeated_rows: number;       // identical-looking charges kept as SEPARATE rows (real repeat purchases)
  period_start: string;        // YYYY-MM-DD, the file's own span (display only)
  period_end: string;
  repeated_groups: number;     // distinct charges that have repeats (repeated_detail may be capped)
  excluded_detail: { description: string; amount: string | number | null; type: string | null }[]; // capped at 50
  repeated_detail: { txn_date: string; description: string | null; amount: string | number; count: number }[]; // capped at 50
  // Absent on summaries persisted before accounts existed.
  new_rows?: number;           // rows this upload added to the account
  already_on_file?: number;    // rows an earlier upload of the account already held
  // Rows whose dates sit inside a finalized period: refused, never imported.
  into_finalized?: {
    count: number;
    periods: { id: number; display_label: string; count: number }[];
    detail: { txn_date: string; amount: string | number; description: string | null }[]; // capped at 50
  };
}

// Event picker option (job-costing). Fetched per-query, not cached in formOptions.
export interface EventOption {
  id: string;
  name: string;
  event_date: string | null;
  location: string | null;
}

// ── Reconciliation (Phase 2B) ────────────────────────────────────────────────
// The unit of work is an ACCOUNT over a BILLING PERIOD. An upload (the statement
// file) is a record under its account, not a work surface.
export type CardProvider = 'chase' | 'amex';

// open (matching in progress) → reconciled (finalized, export
// unlocked) → exported (CSV downloaded at least once) → synced (pushed to QBO).
export type PeriodStatus = 'open' | 'reconciled' | 'exported' | 'synced';

// Per-period resolution rollup on GET /accounts. Distinct from ReconBoard.tally,
// which the board computes with review/autoConfirmed on top.
export interface PeriodTally {
  total: number;
  matched: number;
  suggested: number;
  unmatched: number;
  no_receipt_ack: number;
  exceptions: number;
  confident: number;
}

export interface BillingPeriod {
  id: number;
  account_id: number;
  start_date: string;          // YYYY-MM-DD
  end_date: string | null;     // the cycle's close; null only on legacy periods not yet re-cut
  display_label: string;       // derived from the dates, "Aug 18 to Sep 17"
  is_current: boolean;         // contains today: the cycle still filling
  status: PeriodStatus;
  finalized_at: string | null;
  tally?: PeriodTally;         // on GET /accounts only
}

export interface CardAccount {
  id: number;
  label: string;
  provider: CardProvider;
  // Day of the month the statement closes (1..31). Periods are cut from it; null
  // on legacy accounts, which refuse imports until it is set.
  closing_day: number | null;
  uploads_count?: number;      // on GET /accounts only
  outside_count?: number;      // charges no period of this account covers
  periods?: BillingPeriod[];   // newest first, on GET /accounts only
}

// GET /accounts/:id/uploads row. The board's `uploads` carry a subset (no
// is_latest or rows_held).
export interface Upload {
  id: number;
  label: string | null;
  format?: string;
  source_filename: string | null;
  period_start: string | null;
  period_end: string | null;
  row_count?: number;
  created_at: string;
  imported_by_name: string | null;
  rows_held?: number;          // charges this upload brought in (the rest were already on file)
  is_latest?: boolean;         // only the account's newest upload can be deleted
  import_summary?: ImportSummary | null;
}

// POST /reconciliation/import response (201, or 200 when the file was seen before).
export interface ImportResult {
  upload: Upload;
  account: CardAccount;
  imported: number;
  suggested?: number;
  duplicate: boolean;
  summary: ImportSummary | null;
  period_ids: number[];        // periods the file's rows landed in, oldest first
}

// Per-match scoring evidence the engine emits (JSONB). Arrives as an object, or
// defensively as a JSON string — parse at the edge (see EvidenceChips.parseBreakdown).
export interface ScoreBreakdown {
  amountExact: boolean;
  impliedTip?: number;      // charge over receipt total (a hand-written tip)
  impliedTipPct?: number;   // that tip as a % of the receipt total
  dateDeltaDays: number;    // 0 = same day
  last4: boolean;           // read card matched the charge's card
  vendorScore: number;      // 0..1 name similarity
  chargeAmbiguous: boolean; // this charge has 2+ plausible receipts
  receiptContested: boolean;// this receipt is a candidate for 2+ charges
  receiptUnreviewed?: boolean; // the receipt's owner never tapped "Looks good" — Dawn's confirm is the verification
}
export interface ReconMatch {
  id: number;
  receipt_id: number;
  match_kind: 'deterministic' | 'fuzzy' | 'manual';
  match_state: 'suggested' | 'confirmed' | 'rejected';
  confidence: string | number | null;
  ambiguous: boolean;
  allocated_amount: string | number | null;
  receipt_public_id: string;
  vendor_name: string | null;
  receipt_total: string | number | null;
  receipt_date: string | null;
  receipt_card_last4: string | null;
  score_breakdown?: ScoreBreakdown | string | null;
  // Category rides the match so Dawn can override it inline and see a buyer's
  // "Not sure" (receipt_category_deferred) that the finalize gate will block on.
  receipt_category_id?: number | null;
  receipt_category_label?: string | null;
  receipt_category_deferred?: boolean;
  receipt_purchase_class_id?: number | null;
  receipt_purchase_class_label?: string | null;
  receipt_class_deferred?: boolean;
  receipt_class_source?: 'auto' | null;   // 'auto' = folder-import card default (flows, badged "Auto")
  receipt_ai_review_perfect?: boolean;     // digest auto-filed it with no buyer review (badged "AI Review Perfect")
  /** The requests this receipt paid for; [] when none. */
  requisitions?: ReceiptRequisitionLink[];
}

/** A request a receipt is linked to, as the reconcile board carries it. */
export interface ReceiptRequisitionLink {
  id: string;
  req_number: string;
  approved_by_name: string | null;
}

// A manual-match candidate from GET /transactions/:id/candidates (best-first, max 20).
export interface MatchCandidate {
  receipt_id: number;
  receipt_public_id: string;
  vendor_name: string | null;
  receipt_date: string | null;
  total: string | number | null;
  tip: string | number | null;
  card_last4: string | null;
  owner_name: string | null;
  vendorScore: number;
  dateDeltaDays: number;
  impliedTip?: number;
}
export interface ReconException { id: number; exception_type: string; note: string | null; category_id: number | null; }
/** The purchase a credit most plausibly reverses, offered so its account can be
 *  adopted in one click. Advisory: a credit is never matched to anything. */
export interface RefundSuggestion {
  card_transaction_id: number;
  txn_date: string;
  description: string | null;
  amount: number;
  card_last4: string | null;
  category_id: number;
  category_label: string | null;
  source: 'receipt' | 'exception';
  receipt_public_id: string | null;
  amount_matches: boolean;
}
export interface ReconTxn {
  id: number;
  txn_date: string;
  post_date: string | null;
  amount: string | number;
  description: string | null;
  card_last4: string | null;
  card_label: string | null;
  card_owner_user_id?: string | null;   // Wave 5b: who this card (and its unmatched charges) belongs to, for nudging
  card_owner_name?: string | null;      // that owner's first name, the name the nudge greets
  match_status: 'unmatched' | 'suggested' | 'matched' | 'no_receipt_ack' | 'ignored';
  match: ReconMatch | null;
  exception: ReconException | null;
  /** Credits only; null on every purchase. */
  refund_suggestion?: RefundSuggestion | null;
  /** 'separate' = she said this charge is not the remembered subscription. */
  recurring_decision?: 'separate' | 'declined_pick' | null;
  /** A remembered subscription vendor on this card, NOT auto-resolved because something looked off. */
  recurring_hint?: RecurringHint | null;
  /** Not remembered, but seen in earlier periods. Advisory. */
  recurring_proposal?: RecurringProposal | null;
  /** Set on every charge whose vendor+card has a memory; the exception does not carry it. */
  recurring_memory_id?: number | null;
  /** The cardholder's answer from /receipts, while the charge sits in No receipt. */
  cardholder_response?: CardholderResponseKind | null;
  cardholder_response_note?: string | null;
  cardholder_responded_at?: string | null;
}

// ── Charges without a receipt (the cardholder's side of No receipt) ─────────
export type CardholderResponseKind = 'no_receipt' | 'suspicious';

/** GET /receipts/missing-charges item: one of the caller's own unmatched charges. */
export interface MissingCharge {
  id: number;
  txn_date: string;            // YYYY-MM-DD
  post_date: string | null;
  /** Signed like every card_transactions amount: NEGATIVE for a purchase. Show the magnitude. */
  amount: string | number;
  description: string | null;
  card_id: number;
  card_label: string;
  card_last4: string | null;
  period_id: number | null;
  period_label: string | null; // "Aug 18 to Sep 17"
  response: null | { kind: CardholderResponseKind; note: string | null; at: string };
}
export type RecurringKind = 'fixed' | 'variable';
export interface RecurringHint {
  memory_id: number;
  kind: RecurringKind;
  /** Positive magnitude, what it cost last time. */
  last_amount: number;
  reason: 'amount_changed' | 'second_in_period' | 'sibling_declined';
}
export interface RecurringProposal {
  /** Distinct earlier periods with this vendor on this card. */
  seen_in_periods: number;
  /** Every sighting had this exact amount. */
  same_amount: boolean;
  last_amount: number;
}
/** GET /reconciliation/recurring item: one remembered subscription on one card. */
export interface RecurringMemory {
  id: number;
  normalized_vendor: string;
  display_vendor: string;
  card_last4: string | null;
  card_label: string | null;
  kind: RecurringKind;
  status: string;
  category_id: number | null;
  category_label: string | null;
  last_amount: number;
  last_seen_on: string | null;
  times_seen: number;
}
export interface ReconBoard {
  account: CardAccount;
  period: BillingPeriod;
  uploads: Upload[];           // the uploads that brought this period's charges in
  outside_count: number;       // charges on the account no period covers
  transactions: ReconTxn[];
  tally: { total: number; matched: number; suggested: number; unmatched: number; exceptions: number; confident: number; review: number; autoConfirmed?: number; recurring?: number };
  qbo: { configured: boolean };
}

// Reconciliation settings (Wave 5a). Financial-gated; loaded once per mount.
export interface ReconSettings {
  autoConfirmSlamDunks: boolean;
}

// Owner-detection candidate from GET /cards/:id/detect-owner (Wave 6). Best-first,
// max 3, may be empty. `evidence` explains the guess; `confidence` is 0..1.
export interface DetectOwnerCandidate {
  user_id: string;
  name: string;
  confidence: number;
  evidence: {
    labelMatch: boolean;    // the owner's name appears in the card label
    labelScore: number;     // 0..1 name-vs-label similarity
    receiptCount: number;   // receipts this user filed on this card
  };
}

// ── QBO sync preview / result (Phase 3) ─────────────────────────────────────
export interface QboPurchasePreview {
  date: string | null;
  amount: string | number | null;
  account_name: string | null;
  memo: string | null;
}
export interface QboSkipped {
  reason: string;
  [key: string]: unknown;
}
export interface QboSyncResult {
  dryRun: boolean;
  count?: number;         // dry-run: payloads that would be created
  synced?: number;        // live: purchases created this run
  alreadySynced?: number; // live: resumed rows skipped (already pushed)
  periodSynced?: boolean;
  purchases?: QboPurchasePreview[];
  skipped?: QboSkipped[];
  errors?: { txnId: number; receipt_public_id?: string; error: string }[];
}

export const EXCEPTION_TYPES: { value: string; label: string }[] = [
  { value: 'no_receipt', label: 'No receipt' },
  { value: 'personal', label: 'Personal charge' },
  { value: 'disputed', label: 'Disputed' },
  { value: 'duplicate_charge', label: 'Duplicate charge' },
  { value: 'pending_receipt', label: 'Receipt pending' },
  { value: 'refund_credit', label: 'Refund / credit' },
  // One-time cutover reason: the receipt was matched in QuickBooks the old way
  // (Drive folders) before this app took over, so no receipt exists in-app.
  { value: 'pre_cutover', label: `Handled before ${instance.displayName}` },
];

/** Label for any exception type, including ones set by a dedicated action rather than
 *  the reason dropdown (so they stay out of EXCEPTION_TYPES). */
export const exceptionTypeLabel = (type: string | null | undefined): string =>
  type === 'recurring' ? 'Recurring subscription' : EXCEPTION_TYPES.find((e) => e.value === type)?.label || type || '';

// ── Edit history (Wave 5b) ───────────────────────────────────────────────────
export interface ReceiptHistoryEntry {
  field: string;                       // column name, e.g. 'vendor_name', 'status', 'line_items'
  old_value: string | number | null;
  new_value: string | number | null;
  edited_by_name: string | null;
  created_at: string;
}

// ── Ready-to-confirm bulk submit (buyer queue) ──────────────────────────────
export interface SubmitCleanSkipped { public_id: string; reason: string; }
export interface SubmitCleanResult { submitted: number; skipped: SubmitCleanSkipped[]; }

// ── Nudges (Wave 5b) ─────────────────────────────────────────────────────────
export interface NudgeResult {
  sent: 'slack' | 'email';
  receiptsPending: number;
  chargesMissing: number;
}
export interface NudgeRecentEntry {
  target_user_id: string;
  channel: 'slack' | 'email';
  created_at: string;
}
export interface NudgeRecent {
  nudges: NudgeRecentEntry[];
}

// ── Spend insights (Wave 5b) ─────────────────────────────────────────────────
// Aggregate rows come straight off Postgres SUM()/COUNT() — arrive as strings,
// same "coerce at the edge" rule as the rest of this file.
export interface SpendByMonth { month: string; total: string | number; count: string | number; }
export interface SpendByCategory { category_id: number | null; label: string; total: string | number; count: string | number; }
export interface SpendByOwner { user_id: string; name: string; total: string | number; count: string | number; }
export interface SpendByEvent { event_id: string; name: string; event_date: string | null; total: string | number; count: string | number; }
export interface SpendTotals {
  total: string | number;
  count: string | number;
  uncategorized: string | number;      // $ with no category_id
  untaggedToEvent: string | number;    // $ with no event_id
}
export interface SpendSummary {
  months: number;
  byMonth: SpendByMonth[];
  byCategory: SpendByCategory[];
  byOwner: SpendByOwner[];
  byEvent: SpendByEvent[];
  totals: SpendTotals;
}
export interface EventSpendReceipt {
  public_id: string;
  vendor_name: string | null;
  receipt_date: string | null;
  total: string | number | null;
}
export interface EventSpend {
  event: { id: string; name: string; event_date: string | null };
  total: string | number;
  count: string | number;
  byCategory: SpendByCategory[];
  receipts: EventSpendReceipt[];
}

// Human label for each attention reason the backend emits.
export const ATTENTION_LABELS: Record<string, string> = {
  math_mismatch: "Line items + tax don't add up to the total",
  line_items_dont_sum: "Line items don't sum to the subtotal",
  missing_total: 'No total was read',
  low_confidence: 'The reader was unsure about this one',
  non_usd: 'Non-USD currency',
  category_unconfirmed: "Pick a category — the reader wasn't sure",
  possible_duplicate: 'Looks like a duplicate of another receipt',
};
